import { AsyncPipe } from '@angular/common';
import { Component, OnInit, OnDestroy, inject, signal } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { LoadingService } from './shared/loading.service';

@Component({
  selector: 'app-root',
  imports: [AsyncPipe, RouterOutlet],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App implements OnInit, OnDestroy {
  protected readonly title = signal('grers_ui');
  protected readonly loadingService = inject(LoadingService);
  private readonly _destroying$ = new Subject<void>();
  private readonly authService = inject(MsalService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private readonly router = inject(Router);
  private loadingTimer: number | undefined;

  ngOnInit(): void {
    this.authService.instance.enableAccountStorageEvents();
    this.showPageLoading();

    this.router.events
      .pipe(takeUntil(this._destroying$))
      .subscribe((event) => {
        if (event instanceof NavigationStart && this.isDashboardOrAdminUrl(event.url)) {
          this.showPageLoading();
          return;
        }

        if (event instanceof NavigationEnd && !this.isDashboardUrl(event.urlAfterRedirects)) {
          this.hidePageLoading();
        }

        if (event instanceof NavigationCancel || event instanceof NavigationError) {
          this.hidePageLoading();
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
      });
  }

  checkAndSetActiveAccount() {
    const activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      const accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }

  private isDashboardOrAdminUrl(url: string): boolean {
    const path = url.split('?')[0].split('#')[0];
    return path === '/' || path === '/admin';
  }

  private isDashboardUrl(url: string): boolean {
    const path = url.split('?')[0].split('#')[0];
    return path === '/';
  }

  private showPageLoading(): void {
    window.clearTimeout(this.loadingTimer);
    this.loadingService.show();
  }

  private hidePageLoading(): void {
    this.loadingTimer = window.setTimeout(() => this.loadingService.hide(), 500);
  }

  ngOnDestroy(): void {
    window.clearTimeout(this.loadingTimer);
    this.loadingService.hide();
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
